"use client";
import { motion } from "framer-motion";
import { Sparkles, Plus } from "lucide-react";
import { useNotebook } from "@/lib/notebook/core/store";

function Particle({ delay = 0, x = 0, size = 4 }) {
  return (
    <motion.span
      className="absolute rounded-full bg-foreground/30 dark:bg-foreground/40"
      style={{ left: `${x}%`, width: size, height: size }}
      initial={{ y: 320, opacity: 0 }}
      animate={{ y: -40, opacity: [0, 0.7, 0] }}
      transition={{
        delay,
        duration: 9 + (delay % 3),
        repeat: Infinity,
        ease: "linear",
      }}
    />
  );
}

export default function EmptyState() {
  const openLauncher = useNotebook((s) => s.openLauncher);
  const locked = useNotebook((s) => s.locked);

  return (
    <div className="relative min-h-[70vh] grid place-items-center">
      {/* ambient particles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {Array.from({ length: 14 }).map((_, i) => (
          <Particle
            key={i}
            x={(i * 7.3) % 100}
            delay={(i * 0.7) % 6}
            size={(i % 4) + 2}
          />
        ))}
      </div>

      <div className="relative text-center max-w-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto h-48 w-40 mb-10"
        >
          {/* stacked paper effect */}
          <motion.div
            className="absolute inset-x-4 top-3 h-full rounded-2xl bg-card/80 page-shadow"
            animate={{ rotate: [-2.4, -2.0, -2.4] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute inset-x-2 top-1.5 h-full rounded-2xl bg-card page-shadow"
            animate={{ rotate: [1.2, 0.8, 1.2] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute inset-0 rounded-2xl bg-card page-shadow flex flex-col p-4 gap-2"
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="h-2 rounded-full bg-foreground/10 w-3/4" />
            <div className="h-2 rounded-full bg-foreground/10 w-1/2" />
            <div className="mt-3 h-1.5 rounded-full bg-foreground/8 w-full" />
            <div className="h-1.5 rounded-full bg-foreground/8 w-5/6" />
            <div className="h-1.5 rounded-full bg-foreground/8 w-4/6" />
            <div className="mt-auto flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-foreground/30" />
              <span className="h-1.5 w-1.5 rounded-full bg-foreground/15" />
              <span className="h-1.5 w-1.5 rounded-full bg-foreground/15" />
            </div>
          </motion.div>
          {/* soft glow */}
          <motion.div
            className="absolute -inset-10 rounded-full blur-3xl opacity-50"
            style={{
              background:
                "radial-gradient(circle, hsl(var(--accent-glow) / 0.25), transparent 60%)",
            }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.55, 0.4] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="font-serif text-4xl sm:text-5xl tracking-tight text-foreground"
        >
          A blank page,
          <br />
          <span className="italic text-muted-foreground">
            waiting for thought.
          </span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="mt-4 text-[15px] text-muted-foreground/90 leading-relaxed"
        >
          Begin writing, or compose your notebook from modular sections. The
          page will breathe along with your ideas.
        </motion.p>

        {!locked ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.7 }}
            className="mt-8 flex flex-col items-center gap-2"
          >
            <button
              onClick={() => openLauncher()}
              className="group h-11 px-5 rounded-full bg-foreground text-background text-[13.5px] font-medium tracking-tight inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform elev-2"
            >
              <span className="h-6 w-6 rounded-full bg-background/15 grid place-items-center">
                <Plus className="h-3.5 w-3.5" />
              </span>
              Add your first section
            </button>
            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-muted-foreground px-2.5 py-1 rounded-full bg-secondary/70">
              <Sparkles className="h-3 w-3" /> ⌘ K
            </span>
          </motion.div>
        ) : null}
      </div>
    </div>
  );
}
